import { LanguageSwitcher } from './components/LanguageSwitcher.js';
import React, { useState } from 'react';
import {
  VideoIcon,
  MicIcon,
  ScreenIcon,
  BroadcastIcon,
  PenToolIcon,
  KanbanBoardIcon,
  CrmIcon,
  CalendarIcon,
  SearchIcon,
  UsersIcon,
  SidebarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ShoppingBagIcon,
  PackageIcon,
  HelpQuestionIcon,
  ShieldGovernanceIcon,
} from './icons';
import { BreakpointState } from './useBreakpoint';
import { useTranslation } from '../i18n/useTranslation.js';
import type { ThemePreset } from '../theme/themeEngine.js';
import { CHANNEL_PLUGIN_MAPPINGS, isPluginIdEnabled } from './channels/channel-plugin-registry.js';
import { ProfileMenuPopover } from './auth/components/ProfileMenuPopover';
import { SovereignModuleSlotRenderer } from './modules/SovereignModuleSlotRenderer.js';
import { PrivateRoomInviteModal } from './channels/PrivateRoomInviteModal.js';

export const ALLOWED_CHANNELS = [
  { id: 'general', name: '#general', icon: '💬', description: 'Town Square & Community' },
  { id: 'private', name: '#private', icon: '🔒', description: 'Private Sovereign Video Room' },
];

interface HeaderProps {
  currentRoom: string;
  onSelectRoom: (room: string) => void;
  activeMedia: { video: boolean; audio: boolean; screen: boolean; broadcast: boolean; whiteboard: boolean; actionPlanner?: boolean; agents?: boolean };
  onToggleMedia: (key: 'video' | 'audio' | 'screen' | 'broadcast' | 'whiteboard' | 'actionPlanner' | 'agents') => void;
  enabledPlugins?: Record<string, boolean>;
  peerCount: number;
  userName: string;
  avatarIcon: string;
  /** Optional avatar image URL. When provided overrides the emoji avatarIcon. */
  avatarUrl?: string;
  onOpenSettings: () => void;
  onToggleSidebar: () => void;
  isSidebarOpen: boolean;
  unreadNotifications: number;
  onToggleNotifications: () => void;
  isCalendarOpen?: boolean;
  onToggleCalendar?: () => void;
  breakpoint: BreakpointState;
  currentTheme?: ThemePreset;
  onSelectTheme?: (preset: ThemePreset) => void;
  onOpenAccountSwitcher?: () => void;
  roleBadge?: string;
  onOpenCommandPalette?: () => void;
  onOpenHelp?: () => void;
  onOpenDePINMarket?: () => void;
  onOpenFleetHealth?: () => void;
  onlineNodeCount?: number;
  totalNodeCount?: number;
  onOpenSuperadminDashboard?: () => void;
  onLogout?: () => void;
  isAdmin?: boolean;
}

// Unified active colour — same blue for every media toggle
// drop-shadow follows the SVG stroke shape, not the element bounding box
const ICON_ON = '#60a5fa';                                       // accent blue — all active states
const ICON_OFF = 'var(--theme-icon-idle, #b0bec5)';              // brighter idle (theme-aware)

// ─── Avatar helpers ───────────────────────────────────────────────────────────
function avatarColorFromString(str: string): string {
  const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4', '#10b981', '#f59e0b'];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}
function nameInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('');
}


const iconBtn = (active: boolean): React.CSSProperties => ({
  padding: '6px 8px',
  borderRadius: '6px',
  border: 'none',
  background: 'transparent',
  color: active ? ICON_ON : ICON_OFF,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  // drop-shadow traces the SVG alpha — produces icon-shaped glow, not a rectangle
  filter: active
    ? `drop-shadow(0 0 3px ${ICON_ON}) drop-shadow(0 0 7px ${ICON_ON}99)`
    : 'none',
  transition: 'color 0.15s ease, filter 0.2s ease',
});

/** Responsive high-DPI avatar — photo if available, initials gradient otherwise */
const AvatarBadgeInHeader: React.FC<{
  name: string;
  avatarIcon?: string;
  avatarUrl?: string;
  size?: number;
}> = ({ name, avatarIcon, avatarUrl, size = 32 }) => {
  const [imgError, setImgError] = React.useState(false);
  const color = avatarColorFromString(name || 'U');
  const inits = nameInitials(name || 'U');
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  // Use higher resolution srcset so avatars are crisp on Retina/high-DPI screens
  const srcSet = avatarUrl && !imgError
    ? `${avatarUrl} 1x, ${avatarUrl} ${Math.min(dpr, 3)}x`
    : undefined;

  if (avatarUrl && !imgError) {
    return (
      <img
        src={avatarUrl}
        srcSet={srcSet}
        alt={name}
        onError={() => setImgError(true)}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          border: `2px solid ${color}`,
          flexShrink: 0,
        }}
      />
    );
  }

  // User icon set by user (emoji / avatar icon)
  if (avatarIcon) {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          border: `2px solid ${color}`,
          fontSize: size * 0.55,
          lineHeight: 1,
        }}
      >
        <span>{avatarIcon}</span>
      </div>
    );
  }

  // Initials gradient fallback
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        border: `2px solid ${color}`,
      }}
    >
      <span style={{ fontSize: size * 0.38, fontWeight: 700, color: '#fff', lineHeight: 1 }}>
        {inits || '?'}
      </span>
    </div>
  );
};



export const Header: React.FC<HeaderProps> = ({
  currentRoom,
  onSelectRoom,
  activeMedia,
  onToggleMedia,
  enabledPlugins,
  peerCount: _peerCount,
  userName,
  avatarIcon,
  avatarUrl,
  onOpenSettings,
  onToggleSidebar,
  isSidebarOpen,
  unreadNotifications: _unreadNotifications,
  onToggleNotifications: _onToggleNotifications,
  isCalendarOpen = false,
  onToggleCalendar,
  breakpoint,
  currentTheme = 'midnight',
  onSelectTheme,
  onOpenAccountSwitcher,
  roleBadge,
  onOpenCommandPalette,
  onOpenHelp,
  onOpenDePINMarket,
  onOpenFleetHealth,
  onlineNodeCount = 5,
  totalNodeCount = 5,
  onOpenSuperadminDashboard,
  onLogout,
  isAdmin,
}) => {
  const { isMobile, bp } = breakpoint;
  const { t } = useTranslation();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  const [hasCamera, setHasCamera] = React.useState(true);
  const [hasMic, setHasMic] = React.useState(true);

  React.useEffect(() => {
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      navigator.mediaDevices.enumerateDevices().then(devices => {
        setHasCamera(devices.some(d => d.kind === 'videoinput'));
        setHasMic(devices.some(d => d.kind === 'audioinput'));
      }).catch(err => console.error('Failed to enumerate devices:', err));
    }
  }, []);

  return (
    <header
      style={{
        height: 'var(--app-header-height, 44px)',
        backgroundColor: 'var(--mono-surface, rgba(13, 17, 23, 0.7))',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderBottom: '1px solid var(--mono-border, rgba(255, 255, 255, 0.08))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 8px',
        flexShrink: 0,
        zIndex: 60,
        color: 'var(--theme-text-primary, #f8fafc)',
        gap: '8px',
      }}
    >
      {/* ── Left: Sidebar Toggle + Brand + Room ────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: '0 0 auto' }}>
        <button
          onClick={onToggleSidebar}
          className="has-tooltip"
          data-tooltip={isSidebarOpen ? 'Fold Directory Listing' : 'Open Directory Listing'}
          aria-label={isSidebarOpen ? 'Fold Directory Listing' : 'Open Directory Listing'}
          style={iconBtn(isSidebarOpen)}
        >
          {isSidebarOpen ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
        </button>

        {/* Brand — hide on xs to save space */}
        {bp !== 'xs' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--theme-accent, #3b82f6)', letterSpacing: '-0.03em' }}>
              Herx
            </div>
            <span
              style={{
                fontSize: '10px',
                padding: '1px 6px',
                borderRadius: '4px',
                background: 'rgba(56, 189, 248, 0.15)',
                color: '#38bdf8',
                border: '1px solid rgba(56, 189, 248, 0.3)',
                fontWeight: 700,
                fontFamily: 'monospace',
              }}
            >
              v1.2.0
            </span>
          </div>
        )}

        {/* Room Selector — visible only in responsive/mobile situations or when sidebar is folded */}
        {(isMobile || !isSidebarOpen) && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'var(--theme-bg-card, #151d2a)',
            padding: '3px 8px',
            borderRadius: '6px',
            border: '1px solid var(--theme-border, #1e293b)',
            minWidth: 0,
          }}>
            <span style={{ color: 'var(--theme-text-secondary, #94a3b8)', fontSize: '13px', marginRight: '4px', flexShrink: 0 }}>#</span>
            <select
              value={currentRoom}
              onChange={(e) => onSelectRoom(e.target.value)}
              aria-label="Select channel"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--theme-text-primary, #f1f5f9)',
                fontWeight: 600,
                fontSize: '13px',
                cursor: 'pointer',
                outline: 'none',
                maxWidth: bp === 'xs' ? '90px' : '160px',
              }}
            >
              {ALLOWED_CHANNELS.map((ch) => (
                <option key={ch.id} value={ch.id}>
                  {ch.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* ── Center: Call / Audio status pill (only active during real-time calls) ── */}
      {(activeMedia.video || activeMedia.audio) && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 10px',
          borderRadius: '16px',
          backgroundColor: 'rgba(34, 197, 94, 0.15)',
          border: '1px solid #22c55e',
          color: '#22c55e',
          fontSize: '12px',
          fontWeight: 600,
        }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#22c55e', display: 'inline-block' }} />
          <span>Call Active</span>
          <button
            onClick={() => onToggleMedia('audio')}
            style={{ background: 'none', border: 'none', color: '#22c55e', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            title={activeMedia.audio ? 'Mute' : 'Unmute'}
            aria-label={activeMedia.audio ? 'Mute' : 'Unmute'}
          >
            <MicIcon size={14} />
          </button>
        </div>
      )}

      {/* ── Center: Media & Subsystem Controls ─────────────────────────────── */}
      {bp !== 'xs' && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          flex: '0 0 auto',
        }}>
          {bp !== 'sm' && (
            <button
              onClick={() => hasCamera ? onToggleMedia('video') : null}
              className="has-tooltip"
              data-tooltip={hasCamera ? "Toggle Video" : "Media is not currently available (no camera detected)"}
              aria-label="Toggle Video"
              style={{ ...iconBtn(activeMedia.video), opacity: hasCamera ? 1 : 0.4, cursor: hasCamera ? 'pointer' : 'not-allowed' }}
            >
              <VideoIcon size={15} />
            </button>
          )}

          <button
            onClick={() => hasMic ? onToggleMedia('audio') : null}
            className="has-tooltip"
            data-tooltip={hasMic ? (activeMedia.audio ? 'Mute Mic' : 'Unmute Mic') : "Media is not currently available (no mic detected)"}
            aria-label={activeMedia.audio ? 'Mute Mic' : 'Unmute Mic'}
            style={{ ...iconBtn(activeMedia.audio), opacity: hasMic ? 1 : 0.4, cursor: hasMic ? 'pointer' : 'not-allowed' }}
          >
            <MicIcon size={15} />
          </button>

          {bp !== 'sm' && (
            <button
              onClick={() => onToggleMedia('screen')}
              className="has-tooltip"
              data-tooltip="Toggle Screen Share"
              aria-label="Toggle Screen Share"
              style={iconBtn(activeMedia.screen)}
            >
              <ScreenIcon size={15} />
            </button>
          )}

          {currentRoom === 'private' && (
            <button
              data-testid="btn-invite-guest"
              onClick={() => setIsInviteOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-600/90 hover:bg-emerald-500 text-white rounded-lg shadow-sm transition ml-2"
            >
              <span>+ Invite Guest</span>
            </button>
          )}
        </div>
      )}


      {/* ── Right: Command Palette + Theme + Language + Account ─────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '0 0 auto' }}>
        {/* Dynamic Micro-Frontend Header Slot */}
        {/* <SovereignModuleSlotRenderer slot="header" /> */}


        {/* Command Palette Trigger */}
        {onOpenCommandPalette && (
          <button
            onClick={onOpenCommandPalette}
            className="has-tooltip"
            data-tooltip="Universal Command Palette (⌘K)"
            aria-label="Universal Command Palette"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              backgroundColor: 'var(--theme-bg-card, #151d2a)',
              border: '1px solid var(--theme-border, #1e293b)',
              borderRadius: 'var(--theme-radius-md, 6px)',
              color: 'var(--theme-text-secondary, #cbd5e1)',
              fontSize: '11px',
              cursor: 'pointer',
            }}
          >
            <SearchIcon size={12} />
            {!isMobile && <span>Search</span>}
            <kbd style={{ fontSize: '9px', padding: '1px 4px', backgroundColor: 'var(--theme-bg-subtle, #020617)', borderRadius: '3px', border: '1px solid var(--theme-border, #1e293b)' }}>
              ⌘K
            </kbd>
          </button>
        )}

        {/* Globalization Language Switcher */}
        <LanguageSwitcher />


        {/* In-Situ Contextual Help */}
        {onOpenHelp && (
          <button
            onClick={onOpenHelp}
            className="has-tooltip"
            data-tooltip="Contextual Help & Route Primer (?)"
            aria-label="Contextual Help"
            style={{
              padding: '4px 8px',
              borderRadius: '6px',
              border: '1px solid var(--theme-border, #1e293b)',
              backgroundColor: 'var(--theme-bg-card, #151d2a)',
              color: 'var(--theme-text-primary, #f8fafc)',
              fontSize: '12px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <HelpQuestionIcon size={14} />
          </button>
        )}

        {/* Account / Principal Switcher */}
        {onOpenAccountSwitcher && (
          <button
            id="header-btn-account"
            onClick={onOpenAccountSwitcher}
            className="has-tooltip"
            aria-label={t('auth.switcherTitle') || 'Identity & Role Governance'}
            data-tooltip={t('auth.switcherTitle') || 'Identity & Role Governance'}
            style={{
              padding: '4px 8px',
              borderRadius: '6px',
              border: '1px solid var(--theme-border, #1e293b)',
              backgroundColor: 'var(--theme-bg-card, #151d2a)',
              color: 'var(--theme-accent, #38bdf8)',
              fontSize: '11px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer',
            }}
          >
            <ShieldGovernanceIcon size={14} />
            {!isMobile && <span>{roleBadge || 'Role'}</span>}
          </button>
        )}



        {/* SuperAdmin Dashboard Direct Launcher */}
        {isAdmin && onOpenSuperadminDashboard && (
          <button
            id="header-btn-superadmin"
            onClick={onOpenSuperadminDashboard}
            className="has-tooltip"
            data-tooltip="SuperAdmin Control Plane Dashboard"
            aria-label="Open SuperAdmin Control Plane Dashboard"
            style={{
              padding: '4px 8px',
              borderRadius: '6px',
              border: '1px solid rgba(168, 85, 247, 0.4)',
              backgroundColor: 'rgba(168, 85, 247, 0.12)',
              color: '#c084fc',
              fontSize: '11px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer',
            }}
          >
            <span>👑</span>
            {!isMobile && <span>Admin</span>}
          </button>
        )}

        {/* Profile Avatar Popover Trigger — Far Top Right Hand Standard */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setIsProfileMenuOpen((prev) => !prev)}
            className="has-tooltip"
            data-tooltip={roleBadge ? `${userName} (${roleBadge})` : userName || t('header.profile_menu') || 'Profile & Multi-Account Menu'}
            aria-label={`User profile and settings for ${userName || 'user'}`}
            aria-haspopup="true"
            aria-expanded={isProfileMenuOpen}
            data-testid="avatar-profile-button"
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              background: 'transparent',
              border: 'none',
              padding: '2px',
              borderRadius: '50%',
              color: 'var(--theme-text-primary, #f1f5f9)',
              opacity: 0.95,
              transition: 'opacity 0.15s ease',
            }}
          >
            <AvatarBadgeInHeader
              name={userName}
              avatarUrl={avatarUrl}
              avatarIcon={avatarIcon}
              size={isMobile ? 32 : 36}
            />
          </button>

          <ProfileMenuPopover
            isOpen={isProfileMenuOpen}
            onClose={() => setIsProfileMenuOpen(false)}
            onOpenSettings={onOpenSettings}
          />
        </div>
      </div>

      <PrivateRoomInviteModal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        roomId="private"
      />
    </header>
  );
};
